/**
 * Servicio de reCAPTCHA
 * Maneja la configuración y obtención de tokens de reCAPTCHA v3
 */

/**
 * Obtiene la site key de reCAPTCHA desde variables de entorno
 * @returns {string} Site key de reCAPTCHA
 */
export const getRecaptchaSiteKey = () => {
  return process.env.REACT_APP_RECAPTCHA_SITE_KEY || '';
};

/**
 * Verifica si reCAPTCHA está configurado
 * @returns {boolean} True si está configurado
 */
export const isRecaptchaConfigured = () => {
  return !!getRecaptchaSiteKey();
};

/**
 * Ejecuta reCAPTCHA v3 y obtiene un token
 * @param {string} action - Acción a ejecutar (ej: 'submit', 'vote')
 * @returns {Promise<string>} Token de reCAPTCHA
 */
/**
 * Obtiene la site key desde el script cargado en el HTML
 * Esto asegura que usemos la misma key que está en el script tag
 */
const getSiteKeyFromScript = () => {
  // Buscar el script de reCAPTCHA en el DOM
  const script = document.querySelector('script[src*="recaptcha/api.js"]');
  if (script && script.src) {
    const match = script.src.match(/render=([^&]+)/);
    if (match && match[1]) {
      return match[1];
    }
  }
  // Fallback a la variable de entorno
  return getRecaptchaSiteKey();
};

export const executeRecaptcha = async (action = 'submit') => {
  return new Promise((resolve, reject) => {
    // Obtener site key del script cargado (más confiable)
    const siteKey = getSiteKeyFromScript();
    
    if (!siteKey) {
      reject(new Error('reCAPTCHA site key no configurada'));
      return;
    }

    // Función para intentar ejecutar reCAPTCHA
    const tryExecute = () => {
      // Verificar que reCAPTCHA esté disponible
      if (typeof window.grecaptcha === 'undefined') {
        reject(new Error('reCAPTCHA no está cargado. Verifica que el script esté incluido.'));
        return;
      }

      try {
        // Ejecutar reCAPTCHA v3
        window.grecaptcha.ready(() => {
          window.grecaptcha
            .execute(siteKey, { action })
            .then((token) => {
              if (!token || token.length === 0) {
                reject(new Error('Token de reCAPTCHA vacío o inválido'));
                return;
              }
              resolve(token);
            })
            .catch((error) => {
              console.error('Error al ejecutar reCAPTCHA:', error);
              reject(error);
            });
        });
      } catch (error) {
        console.error('Error al inicializar reCAPTCHA:', error);
        reject(error);
      }
    };

    // Si ya está cargado, ejecutar inmediatamente
    if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
      tryExecute();
    } else {
      // Esperar a que se cargue (máximo 10 segundos)
      let attempts = 0;
      const maxAttempts = 100; // 10 segundos (100 * 100ms)
      
      const checkInterval = setInterval(() => {
        attempts++;
        if (window.grecaptcha && typeof window.grecaptcha.ready === 'function') {
          clearInterval(checkInterval);
          tryExecute();
        } else if (attempts >= maxAttempts) {
          clearInterval(checkInterval);
          reject(new Error('reCAPTCHA no se cargó a tiempo. Verifica tu conexión y que el dominio esté autorizado en Google reCAPTCHA Admin.'));
        }
      }, 100);
    }
  });
};

/**
 * Carga el script de reCAPTCHA dinámicamente
 * @returns {Promise<void>}
 */
export const loadRecaptchaScript = () => {
  return new Promise((resolve, reject) => {
    // Verificar si ya está cargado
    if (window.grecaptcha) {
      resolve();
      return;
    }

    // Verificar si el script ya está en el DOM
    const existingScript = document.querySelector('script[src*="recaptcha"]');
    if (existingScript) {
      // Esperar a que se cargue
      existingScript.addEventListener('load', () => resolve());
      existingScript.addEventListener('error', reject);
      return;
    }

    // Crear y cargar el script
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${getRecaptchaSiteKey()}`;
    script.async = true;
    script.defer = true;
    
    script.onload = () => {
      // Esperar a que grecaptcha esté listo
      if (window.grecaptcha) {
        window.grecaptcha.ready(() => resolve());
      } else {
        resolve();
      }
    };
    
    script.onerror = () => {
      reject(new Error('Error al cargar script de reCAPTCHA'));
    };
    
    document.head.appendChild(script);
  });
};

